import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, TextField, Button,
  Alert, CircularProgress, Slider,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import millingApi from '../../../services/millingApi';

const empty = {
  scheduleDate: new Date().toISOString().split('T')[0],
  millingOrderId: '',
  orderNumber: '',
  recipeName: '',
  millId: '',
  millName: '',
  plannedStartTime: '',
  plannedEndTime: '',
  plannedInputQtyMt: '',
  plannedOutputQtyMt: '',
  priority: 3,
  assignedTo: '',
  notes: '',
};

const numKeys = ['plannedInputQtyMt', 'plannedOutputQtyMt'];

const MillingScheduleForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<any>(empty);

  useEffect(() => {
    if (!isEdit) return;
    millingApi.getMillingScheduleEntry(id!).then((r) => {
      const f: any = { ...empty };
      Object.keys(empty).forEach((k) => { f[k] = r[k] != null ? (k === 'priority' ? Number(r[k]) : String(r[k])) : (k === 'priority' ? 3 : ''); });
      setForm(f);
    }).catch(() => setError('Failed to load entry')).finally(() => setLoading(false));
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    const payload: any = { ...form };
    numKeys.forEach((k) => { payload[k] = payload[k] !== '' ? Number(payload[k]) : undefined; });
    if (!payload.millingOrderId) delete payload.millingOrderId;
    if (!payload.millId) delete payload.millId;
    if (!payload.plannedStartTime) delete payload.plannedStartTime;
    if (!payload.plannedEndTime) delete payload.plannedEndTime;
    try {
      if (isEdit) await millingApi.updateMillingScheduleEntry(id!, payload);
      else await millingApi.createMillingScheduleEntry(payload);
      navigate('/milling/schedule');
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ p: 3, maxWidth: 800 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <Button onClick={() => navigate('/milling/schedule')}>← Back</Button>
        <Typography variant="h5">{isEdit ? 'Edit Schedule Entry' : 'New Schedule Entry'}</Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Schedule Details</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Schedule Date *" type="date" fullWidth size="small" value={form.scheduleDate}
                  onChange={(e) => setForm((f: any) => ({ ...f, scheduleDate: e.target.value }))}
                  InputLabelProps={{ shrink: true }} required />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Order Number" fullWidth size="small" value={form.orderNumber}
                  onChange={(e) => setForm((f: any) => ({ ...f, orderNumber: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Recipe Name" fullWidth size="small" value={form.recipeName}
                  onChange={(e) => setForm((f: any) => ({ ...f, recipeName: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Mill ID" fullWidth size="small" value={form.millId}
                  onChange={(e) => setForm((f: any) => ({ ...f, millId: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Mill Name" fullWidth size="small" value={form.millName}
                  onChange={(e) => setForm((f: any) => ({ ...f, millName: e.target.value }))} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Assigned To" fullWidth size="small" value={form.assignedTo}
                  onChange={(e) => setForm((f: any) => ({ ...f, assignedTo: e.target.value }))} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Timing & Quantities</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Planned Start Time" type="time" fullWidth size="small" value={form.plannedStartTime}
                  onChange={(e) => setForm((f: any) => ({ ...f, plannedStartTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Planned End Time" type="time" fullWidth size="small" value={form.plannedEndTime}
                  onChange={(e) => setForm((f: any) => ({ ...f, plannedEndTime: e.target.value }))}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Planned Input (MT)" type="number" fullWidth size="small" value={form.plannedInputQtyMt}
                  onChange={(e) => setForm((f: any) => ({ ...f, plannedInputQtyMt: e.target.value }))}
                  inputProps={{ step: 0.1, min: 0 }} />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField label="Planned Output (MT)" type="number" fullWidth size="small" value={form.plannedOutputQtyMt}
                  onChange={(e) => setForm((f: any) => ({ ...f, plannedOutputQtyMt: e.target.value }))}
                  inputProps={{ step: 0.1, min: 0 }} />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>Priority (1 = Highest, 5 = Lowest)</Typography>
            <Box sx={{ px: 2 }}>
              <Slider value={form.priority} min={1} max={5} step={1} marks
                valueLabelDisplay="on"
                onChange={(_, v) => setForm((f: any) => ({ ...f, priority: v as number }))}
                color={form.priority <= 2 ? 'error' : form.priority === 3 ? 'warning' : 'primary'}
              />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ mb: 2 }}>
          <CardContent>
            <TextField label="Notes" fullWidth multiline rows={2} value={form.notes}
              onChange={(e) => setForm((f: any) => ({ ...f, notes: e.target.value }))} />
          </CardContent>
        </Card>

        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button variant="contained" type="submit" disabled={saving}>
            {saving ? 'Saving...' : isEdit ? 'Update Entry' : 'Save Entry'}
          </Button>
          <Button onClick={() => navigate('/milling/schedule')}>Cancel</Button>
        </Box>
      </form>
    </Box>
  );
};

export default MillingScheduleForm;
